import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Spinner,
  Alert,
  Badge,
  Modal,
  Form,
} from 'react-bootstrap';
import { hiringDirectionsApiService } from '../../services/apiServices';
import { RecruitingDirectionDto } from '../../api-client';

const HiringDirectionsListPage: React.FC = () => {
  const [directions, setDirections] = useState<RecruitingDirectionDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [selectedDirection, setSelectedDirection] = useState<RecruitingDirectionDto | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const [updating, setUpdating] = useState<boolean>(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [newDirectionName, setNewDirectionName] = useState<string>('');
  const [newDirectionDescription, setNewDirectionDescription] = useState<string>('');
  const [editDirectionName, setEditDirectionName] = useState<string>('');
  const [editDirectionDescription, setEditDirectionDescription] = useState<string>('');

  const loadDirections = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await hiringDirectionsApiService.getDirections();
      setDirections(response.data || []);
    } catch (err: any) {
      console.error('Ошибка при загрузке направлений:', err);
      setError(err.message || 'Ошибка при загрузке направлений');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDirections();
  }, [loadDirections]);

  const handleCreateDirection = async () => {
    if (!newDirectionName.trim()) {
      setCreateError('Название направления обязательно');
      return;
    }

    try {
      setCreating(true);
      setCreateError(null);
      await hiringDirectionsApiService.createDirection(
        newDirectionName.trim(),
        newDirectionDescription.trim() || null,
        true
      );

      setShowCreateModal(false);
      setNewDirectionName('');
      setNewDirectionDescription('');
      loadDirections();
    } catch (err: any) {
      console.error('Ошибка при создании направления:', err);
      setCreateError(err.message || 'Ошибка при создании направления');
    } finally {
      setCreating(false);
    }
  };

  const handleActivateDirection = async (directionId: string) => {
    try {
      await hiringDirectionsApiService.activateDirection(directionId);
      loadDirections();
    } catch (err: any) {
      console.error('Ошибка при активации направления:', err);
      setError(err.message || 'Ошибка при активации направления');
    }
  };

  const handleDeactivateDirection = async (directionId: string) => {
    try {
      await hiringDirectionsApiService.deactivateDirection(directionId);
      loadDirections();
    } catch (err: any) {
      console.error('Ошибка при деактивации направления:', err);
      setError(err.message || 'Ошибка при деактивации направления');
    }
  };

  const handleShowCreateModal = () => {
    setShowCreateModal(true);
    setCreateError(null);
    setNewDirectionName('');
    setNewDirectionDescription('');
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateError(null);
    setNewDirectionName('');
    setNewDirectionDescription('');
  };

  const handleShowEditModal = (direction: RecruitingDirectionDto) => {
    setSelectedDirection(direction);
    setEditDirectionName(direction.name);
    setEditDirectionDescription(direction.description || '');
    setShowEditModal(true);
    setUpdateError(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedDirection(null);
    setUpdateError(null);
  };

  const handleUpdateDirection = async () => {
    if (!selectedDirection || !editDirectionName.trim()) {
      setUpdateError('Название направления обязательно');
      return;
    }

    try {
      setUpdating(true);
      setUpdateError(null);
      await hiringDirectionsApiService.updateDirection(
        selectedDirection.id,
        editDirectionName.trim(),
        editDirectionDescription.trim() || null
      );

      setShowEditModal(false);
      setSelectedDirection(null);
      loadDirections();
    } catch (err: any) {
      console.error('Ошибка при обновлении направления:', err);
      setUpdateError(err.message || 'Ошибка при обновлении направления');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Направления рекрутинга</h2>
            <Button variant="primary" onClick={handleShowCreateModal}>
              Создать направление
            </Button>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" />
              <p className="mt-2">Загрузка направлений...</p>
            </div>
          ) : (
            <>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Название</th>
                    <th>Описание</th>
                    <th>Статус</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {directions && directions.length > 0 ? (
                    directions.map((direction) => (
                      <tr key={direction.id}>
                        <td>{direction.id}</td>
                        <td>
                          <strong>{direction.name}</strong>
                        </td>
                        <td>{direction.description || '—'}</td>
                        <td>
                          <Badge bg={direction.is_active ? 'success' : 'secondary'}>
                            {direction.is_active ? 'Активно' : 'Неактивно'}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            onClick={() => handleShowEditModal(direction)}
                          >
                            Редактировать
                          </Button>
                          {direction.is_active ? (
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => handleDeactivateDirection(direction.id)}
                            >
                              Деактивировать
                            </Button>
                          ) : (
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleActivateDirection(direction.id)}
                            >
                              Активировать
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center">
                        Направления не найдены
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </>
          )}
        </Col>
      </Row>

      {/* Модальное окно создания направления */}
      <Modal show={showCreateModal} onHide={handleCloseCreateModal}>
        <Modal.Header closeButton>
          <Modal.Title>Создать направление</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createError && <Alert variant="danger">{createError}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Название *</Form.Label>
              <Form.Control
                type="text"
                value={newDirectionName}
                onChange={(e) => setNewDirectionName(e.target.value)}
                placeholder="Введите название направления"
                isInvalid={!!createError && !newDirectionName.trim()}
              />
              <Form.Control.Feedback type="invalid">Название обязательно</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Описание</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newDirectionDescription}
                onChange={(e) => setNewDirectionDescription(e.target.value)}
                placeholder="Введите описание направления (необязательно)"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCreateModal}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleCreateDirection} disabled={creating}>
            {creating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Создание...
              </>
            ) : (
              'Создать'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно редактирования направления */}
      <Modal show={showEditModal} onHide={handleCloseEditModal}>
        <Modal.Header closeButton>
          <Modal.Title>Редактировать направление</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {updateError && <Alert variant="danger">{updateError}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Название *</Form.Label>
              <Form.Control
                type="text"
                value={editDirectionName}
                onChange={(e) => setEditDirectionName(e.target.value)}
                placeholder="Введите название направления"
                isInvalid={!!updateError && !editDirectionName.trim()}
              />
              <Form.Control.Feedback type="invalid">Название обязательно</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Описание</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editDirectionDescription}
                onChange={(e) => setEditDirectionDescription(e.target.value)}
                placeholder="Введите описание направления (необязательно)"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditModal}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleUpdateDirection} disabled={updating}>
            {updating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Сохранение...
              </>
            ) : (
              'Сохранить'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default HiringDirectionsListPage;
